import { useEffect, useRef, useState } from 'react';
import { Box, Maximize2, Rotate3D, ZoomIn } from 'lucide-react';

export default function StlPreview({ stlUrl, status }) {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(Boolean(stlUrl));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!stlUrl || !mountRef.current) {
      return undefined;
    }

    let disposed = false;
    let renderer;
    let controls;
    let animationFrame;

    async function initPreview() {
      setLoading(true);
      setError('');
      try {
        const THREE = await import('three');
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');
        const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');

        if (disposed || !mountRef.current) return;

      const mount = mountRef.current;
      const width = mount.clientWidth || 720;
      const height = mount.clientHeight || 420;
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf4f7f7);

        const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 1000);
        camera.position.set(58, 54, 82);

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        mount.replaceChildren(renderer.domElement);

        const ambient = new THREE.HemisphereLight(0xffffff, 0xa9b5b5, 2.25);
        scene.add(ambient);
        const key = new THREE.DirectionalLight(0xffffff, 2.35);
        key.position.set(48, 64, 38);
        scene.add(key);
        const rim = new THREE.DirectionalLight(0x8ce4dc, 1.2);
        rim.position.set(-40, 35, -20);
        scene.add(rim);

        const grid = new THREE.GridHelper(84, 24, 0x9aa7a7, 0xd7dddd);
        grid.position.y = -0.1;
        scene.add(grid);

        const loader = new STLLoader();
        const geometry = await loader.loadAsync(stlUrl);
        geometry.computeVertexNormals();
        geometry.center();

        const material = new THREE.MeshStandardMaterial({
          color: 0xa7adad,
          roughness: 0.76,
          metalness: 0.08
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.rotation.x = -Math.PI / 2;
        scene.add(mesh);

        const box = new THREE.Box3().setFromObject(mesh);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3()).length();
        controls = new OrbitControls(camera, renderer.domElement);
        controls.target.copy(center);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.minDistance = 30;
        controls.maxDistance = 180;
        camera.position.set(size * 0.95, size * 0.82, size * 1.25);
        controls.update();

        const resizeObserver = new ResizeObserver(() => {
          if (!mountRef.current || !renderer) return;
          const nextWidth = mountRef.current.clientWidth || width;
          const nextHeight = mountRef.current.clientHeight || height;
          camera.aspect = nextWidth / nextHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(nextWidth, nextHeight);
        });
        resizeObserver.observe(mount);

        const animate = () => {
          controls.update();
          renderer.render(scene, camera);
          animationFrame = window.requestAnimationFrame(animate);
        };
        animate();

        setLoading(false);

        return () => {
          resizeObserver.disconnect();
        };
      } catch (previewError) {
        setError('Preview could not load, but your STL is still ready to download.');
        setLoading(false);
        console.error(previewError);
      }
    }

    let cleanupResize;
    initPreview().then((cleanup) => {
      cleanupResize = cleanup;
    });

    return () => {
      disposed = true;
      if (cleanupResize) cleanupResize();
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      if (controls) controls.dispose();
      if (renderer) {
        renderer.dispose();
        if (renderer.domElement.parentNode) {
          renderer.domElement.remove();
        }
      }
    };
  }, [stlUrl]);

  return (
    <div className="preview-shell" aria-live="polite">
      <div className="preview-toolbar">
        <span className={`status-dot ${status === 'completed' ? 'ready' : ''}`} />
        <span>{status === 'completed' ? 'STL preview ready' : 'Awaiting generated STL'}</span>
        <div className="preview-tools" aria-hidden="true">
          <Rotate3D size={18} />
          <ZoomIn size={18} />
          <Maximize2 size={17} />
        </div>
      </div>
      <div className="preview-stage">
        <div className="canvas-host" ref={mountRef} />
        {!stlUrl && (
          <div className="empty-preview">
            <Box size={46} strokeWidth={1.5} />
            <span>Generated STL preview appears here</span>
          </div>
        )}
        {loading && stlUrl && <div className="loading-preview">Loading WebGL preview...</div>}
        {error && <div className="loading-preview error">{error}</div>}
      </div>
      <div className="gesture-strip">Drag to rotate <span /> Scroll to zoom <span /> Right-click to pan</div>
    </div>
  );
}
